/**
 * PuzzleScene - Chess puzzle solving scene
 * Player must find the correct sequence of moves
 */

import Phaser from 'phaser';
import { ChessGame } from '../game/chess/ChessGame';
import { ChessClock } from '../game/ChessClock';
import { PuzzleDatabase } from '../game/puzzles/PuzzleDatabase';
import { GameStateManager } from '../game/GameStateManager';
import { PuzzleData, Square } from '../types';
import { AI_DIFFICULTY, GAME_CONFIG } from '../constants/GameConstants';
import { T, INK, PALETTE, makeButton, makePanel, paintBackground, fadeInScene } from '../constants/Theme';
import { AudioManager } from '../game/AudioManager';

export class PuzzleScene extends Phaser.Scene {
  private chessGame!: ChessGame;
  private chessClock!: ChessClock;
  private puzzleData!: PuzzleData;
  private gameStateManager!: GameStateManager;

  // Board rendering
  private readonly BOARD_SIZE = 8;
  private CELL_SIZE = 70;
  private BOARD_OFFSET_X = 0;
  private BOARD_OFFSET_Y = 0;

  // UI elements
  private pieceSprites: Map<Square, Phaser.GameObjects.Sprite> = new Map();
  private highlightGraphics?: Phaser.GameObjects.Graphics;
  private selectedSquare?: Square;
  private playerClockText?: Phaser.GameObjects.Text;
  private instructionText?: Phaser.GameObjects.Text;
  private hintText?: Phaser.GameObjects.Text;
  private moveListText?: Phaser.GameObjects.Text;

  // Puzzle state
  private puzzleDifficulty: 'beginner' | 'easy' | 'medium' | 'hard' | 'expert' = 'medium';
  private readonly PUZZLE_TIME_MS = 300000; // 5 minutes
  private playerColor: 'w' | 'b' = 'w'; // Player's color in the puzzle
  private redoUsed = false; // Solution Vision (puzzle_redo_once) tracking
  private wrongMoves = 0;    // strikes used (fail on the 3rd)
  private readonly MAX_STRIKES = 3;
  private strikeMarks: Phaser.GameObjects.Text[] = [];

  constructor() {
    super({ key: 'PuzzleScene' });
  }

  init(data?: { difficulty?: 'beginner' | 'easy' | 'medium' | 'hard' | 'expert' }): void {
    if (data && data.difficulty) {
      this.puzzleDifficulty = data.difficulty;
    } else {
      // Scale puzzle difficulty with run progress
      const floor = GameStateManager.getInstance().getCurrentFloor();
      this.puzzleDifficulty =
        floor <= 2 ? 'beginner' :
        floor <= 5 ? 'easy' :
        floor <= 8 ? 'medium' :
        floor <= 11 ? 'hard' : 'expert';
    }
    this.redoUsed = false;
    this.wrongMoves = 0;
    this.strikeMarks = [];
  }

  create(): void {
    this.gameStateManager = GameStateManager.getInstance();

    // Select a random puzzle
    const puzzle = PuzzleDatabase.getPuzzleByTier(this.puzzleDifficulty);

    // Parse FEN to determine player's color (whose turn it is)
    this.playerColor = this.getActiveColorFromFen(puzzle.fen);

    // Initialize puzzle data
    this.puzzleData = {
      puzzle,
      currentMoveIndex: 0,
      playerMoves: [],
      hintsUsed: 0,
      completed: false,
      failed: false
    };

    // Initialize chess game with puzzle position (no obstacles for puzzles)
    // Ranks are 1-indexed in createBoardConfig
    this.chessGame = new ChessGame(puzzle.fen, AI_DIFFICULTY[1], {
      minCol: 0,
      maxCol: 7,
      minRank: 1,
      maxRank: 8
    });

    // Initialize chess clock
    this.chessClock = new ChessClock(this.PUZZLE_TIME_MS, true);
    this.chessClock.setOnPlayerTimeout(() => this.onTimeOut());
    this.chessClock.setOnTick((data) => this.updateClockDisplay(data));

    // Calculate layout
    this.calculateLayout();

    // Create UI
    this.createBackground();
    this.createHeader();
    this.createBoard();
    this.createPieces();
    this.createBottomUI();
    this.createStrikeMarks();
    this.createInputHandlers();

    // Apply puzzle upgrades
    this.applyPuzzleUpgrades();

    // Start the clock
    this.chessClock.start();

    // First-ever puzzle: explain what's going on (pauses the clock while shown)
    this.maybeShowPuzzleIntro();

    console.log('[PuzzleScene] Puzzle loaded:', puzzle.id, 'Rating:', puzzle.rating);
  }

  private applyPuzzleUpgrades(): void {
    const gsm = this.gameStateManager;

    // puzzle_time_15: +15 seconds
    if (gsm.hasActiveUpgrade('puzzle_time_15')) {
      this.chessClock.addPlayerTime(15000);
    }

    // puzzle_hint: auto-show hint for free (does not count against gold)
    if (gsm.hasActiveUpgrade('puzzle_hint')) {
      this.time.delayedCall(500, () => this.revealHint(false));
    }

    // auto_solve_puzzle: add auto-solve button
    if (gsm.hasActiveUpgrade('auto_solve_puzzle')) {
      this.createAutoSolveButton();
    }
  }

  private createAutoSolveButton(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const buttonY = HEIGHT - 50;

    const autoSolve = makeButton(this, WIDTH / 2, buttonY, '⚡ AUTO-SOLVE', {
      width: 260, height: 52, fontSize: 20, fill: PALETTE.gold, textColor: '#10182a',
      onClick: () => {
        this.puzzleData.completed = true;
        this.chessClock.pause();
        this.instructionText?.setText('Puzzle auto-solved!');
        this.instructionText?.setColor(INK.gold);
        autoSolve.container.destroy();
        this.time.delayedCall(1000, () => this.endPuzzle(true, 20));
      },
    });
  }

  update(): void {
    // Update chess clock
    this.chessClock.update();
  }

  private calculateLayout(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Calculate cell size to fit board on screen
    // Reserve space: header (120px), bottom UI (300px)
    const headerSpace = 120;
    const bottomUISpace = 300;
    const availableHeight = HEIGHT - headerSpace - bottomUISpace;
    const availableWidth = WIDTH - 40; // 20px padding on each side

    // Calculate cell size to fit
    const maxCellWidth = availableWidth / this.BOARD_SIZE;
    const maxCellHeight = availableHeight / this.BOARD_SIZE;
    this.CELL_SIZE = Math.min(maxCellWidth, maxCellHeight);

    // Center the board
    const boardWidth = this.BOARD_SIZE * this.CELL_SIZE;
    this.BOARD_OFFSET_X = (WIDTH - boardWidth) / 2;
    this.BOARD_OFFSET_Y = headerSpace;
  }

  private createBackground(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);
  }

  private createHeader(): void {
    const { WIDTH } = GAME_CONFIG;

    // Title
    this.add.text(WIDTH / 2, 24, 'CHESS PUZZLE', T.heading({ color: INK.gold })).setOrigin(0.5).setLetterSpacing(2);

    // Difficulty + rating
    const difficultyText = `${this.puzzleDifficulty.toUpperCase()}  ·  RATING ${this.puzzleData.puzzle.rating}`;
    this.add.text(WIDTH / 2, 62, difficultyText, T.small({ color: INK.secondary })).setOrigin(0.5).setLetterSpacing(1);

    // Clock display
    this.playerClockText = this.add.text(WIDTH / 2, 98, '5:00', T.heading({ color: INK.green })).setOrigin(0.5);

  }

  /** Three dim X slots at the bottom that light up red as mistakes are made. */
  private createStrikeMarks(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const spacing = 64;
    const startX = WIDTH / 2 - spacing;
    const y = HEIGHT - 38;
    this.strikeMarks = [];
    for (let i = 0; i < this.MAX_STRIKES; i++) {
      const x = this.add.text(startX + i * spacing, y, '✗', {
        fontFamily: T.body().fontFamily, fontSize: '44px', color: '#3a4658',
      });
      x.setOrigin(0.5);
      this.strikeMarks.push(x);
    }
  }

  /** Light up the most recent strike's X with a dramatic pop. */
  private lightStrikeMark(): void {
    const idx = this.wrongMoves - 1;
    const mark = this.strikeMarks[idx];
    if (!mark) return;
    mark.setColor('#ef6a5a');
    mark.setScale(2.2);
    mark.setAngle(-12);
    this.tweens.add({ targets: mark, scale: 1, angle: 0, duration: 320, ease: 'Back.out' });
  }

  /** One-time intro the first time the player ever opens a puzzle. */
  private maybeShowPuzzleIntro(): void {
    let seen = false;
    try { seen = localStorage.getItem('chessrogue_puzzle_intro_seen') === '1'; } catch { seen = false; }
    if (seen) return;

    this.chessClock.pause();
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const depth = 500;
    const els: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.85).setOrigin(0);
    overlay.setDepth(depth); overlay.setInteractive(); els.push(overlay);

    const panelH = 470;
    const panel = makePanel(this, cx, HEIGHT / 2, 600, panelH, { fill: PALETTE.panel, stroke: PALETTE.gold, strokeWidth: 2, radius: 24 });
    panel.setDepth(depth + 1); els.push(panel);

    const top = HEIGHT / 2 - panelH / 2;
    const title = this.add.text(cx, top + 44, 'CHESS PUZZLE', T.heading({ color: INK.gold }));
    title.setOrigin(0.5); title.setLetterSpacing(2); title.setDepth(depth + 2); els.push(title);

    const tips: Array<[string, string]> = [
      ['♚', 'Each puzzle is a guaranteed forced checkmate — find the winning move.'],
      ['◎', 'Tap a piece, then a highlighted square. The opponent replies automatically.'],
      ['✗', 'You get three tries. Three wrong moves and the puzzle is lost.'],
      ['⏱', 'Beat the clock and avoid mistakes for the most bonus gold.'],
    ];
    tips.forEach(([icon, text], i) => {
      const y = top + 100 + i * 76;
      const ic = this.add.text(cx - 250, y, icon, { fontFamily: T.body().fontFamily, fontSize: '28px', color: INK.gold });
      ic.setOrigin(0.5, 0); ic.setDepth(depth + 2); els.push(ic);
      const tx = this.add.text(cx - 210, y, text, T.body({ color: INK.secondary, wordWrap: { width: 430 }, lineSpacing: 3 }));
      tx.setOrigin(0, 0); tx.setDepth(depth + 2); els.push(tx);
    });

    const btn = makeButton(this, cx, top + panelH - 52, 'SOLVE IT', {
      width: 300, fill: PALETTE.green, depth: depth + 2,
      onClick: () => {
        try { localStorage.setItem('chessrogue_puzzle_intro_seen', '1'); } catch { /* ignore */ }
        for (const e of els) e.destroy();
        btn.container.destroy();
        this.chessClock.resume();
      },
    });
  }

  private createBoard(): void {
    const boardGraphics = this.add.graphics();

    for (let row = 0; row < this.BOARD_SIZE; row++) {
      for (let col = 0; col < this.BOARD_SIZE; col++) {
        // Flip the color pattern if playing as black to maintain correct perspective
        const displayRow = this.playerColor === 'b' ? 7 - row : row;
        const displayCol = this.playerColor === 'b' ? 7 - col : col;
        const isLight = (displayRow + displayCol) % 2 === 0;
        const color = isLight ? 0xf0d9b5 : 0xb58863;

        boardGraphics.fillStyle(color);
        boardGraphics.fillRect(
          this.BOARD_OFFSET_X + col * this.CELL_SIZE,
          this.BOARD_OFFSET_Y + row * this.CELL_SIZE,
          this.CELL_SIZE,
          this.CELL_SIZE
        );
      }
    }

    // Add file labels (a-h) below board - flip if playing as black
    for (let col = 0; col < this.BOARD_SIZE; col++) {
      const fileIndex = this.playerColor === 'b' ? 7 - col : col;
      const file = String.fromCharCode(97 + fileIndex);
      this.add.text(
        this.BOARD_OFFSET_X + col * this.CELL_SIZE + this.CELL_SIZE / 2,
        this.BOARD_OFFSET_Y + this.BOARD_SIZE * this.CELL_SIZE + 5,
        file,
        { fontSize: '14px', color: '#ffffff' }
      ).setOrigin(0.5, 0);
    }

    // Add rank labels (1-8) to the left - flip if playing as black
    for (let row = 0; row < this.BOARD_SIZE; row++) {
      const rankValue = this.playerColor === 'b' ? row + 1 : 8 - row;
      const rank = rankValue.toString();
      this.add.text(
        this.BOARD_OFFSET_X - 10,
        this.BOARD_OFFSET_Y + row * this.CELL_SIZE + this.CELL_SIZE / 2,
        rank,
        { fontSize: '14px', color: '#ffffff' }
      ).setOrigin(1, 0.5);
    }

    // Create highlight graphics (for selected square and legal moves)
    this.highlightGraphics = this.add.graphics();
    this.highlightGraphics.setDepth(5);
  }

  private createPieces(): void {
    // Clear existing pieces
    this.pieceSprites.forEach(sprite => sprite.destroy());
    this.pieceSprites.clear();

    // Get current board state from chess game
    const board = this.chessGame.getBoard();

    // Iterate through the board and create sprites
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          const file = String.fromCharCode(97 + col);
          const rank = (8 - row).toString();
          const square = (file + rank) as Square;
          this.createPieceSprite(square, piece.type, piece.color);
        }
      }
    }
  }

  private createPieceSprite(square: Square, type: string, color: 'w' | 'b'): void {
    const { x, y } = this.squareToPixel(square);

    // Use lowercase sprite keys: 'wp', 'wn', 'wb', etc.
    const spriteKey = `${color}${type}`;

    if (!this.textures.exists(spriteKey)) {
      console.warn(`[PuzzleScene] Sprite texture not found: ${spriteKey}`);
      return;
    }

    const sprite = this.add.sprite(x, y, spriteKey);
    sprite.setDisplaySize(this.CELL_SIZE * 0.8, this.CELL_SIZE * 0.8);
    sprite.setInteractive();
    sprite.setDepth(10);

    this.pieceSprites.set(square, sprite);
  }

  private createBottomUI(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const bottomY = this.BOARD_OFFSET_Y + this.BOARD_SIZE * this.CELL_SIZE + 35;

    // Instruction text
    this.instructionText = this.add.text(WIDTH / 2, bottomY, 'Find the best move!', T.bodyStrong({ color: INK.primary })).setOrigin(0.5);

    // Puzzle description
    if (this.puzzleData.puzzle.description) {
      this.add.text(WIDTH / 2, bottomY + 32, this.puzzleData.puzzle.description, T.small({
        color: INK.secondary, wordWrap: { width: WIDTH - 60 }, align: 'center',
      })).setOrigin(0.5);
    }

    // Themes
    const themesText = this.puzzleData.puzzle.themes.join(', ');
    this.add.text(WIDTH / 2, bottomY + 62, themesText, T.micro({
      color: INK.faint, wordWrap: { width: WIDTH - 40 }, align: 'center',
    })).setOrigin(0.5);

    // Move list
    this.moveListText = this.add.text(WIDTH / 2, bottomY + 88, 'Moves: -', T.small({
      color: INK.secondary, wordWrap: { width: WIDTH - 40 }, align: 'center',
    })).setOrigin(0.5);

    // Hint text
    this.hintText = this.add.text(WIDTH / 2, bottomY + 114, '', T.small({
      color: INK.gold, wordWrap: { width: WIDTH - 40 }, align: 'center',
    })).setOrigin(0.5);

    // Buttons at bottom
    const buttonY = HEIGHT - 100;
    const buttonWidth = 140;
    const buttonSpacing = 20;
    const totalWidth = buttonWidth * 2 + buttonSpacing;
    const startX = (WIDTH - totalWidth) / 2;

    // Hint + Give Up buttons
    makeButton(this, startX + buttonWidth / 2, buttonY + 26, 'HINT', {
      width: buttonWidth, height: 56, fontSize: 22, fill: PALETTE.blue, onClick: () => this.showHint(),
    });
    makeButton(this, startX + buttonWidth + buttonSpacing + buttonWidth / 2, buttonY + 26, 'GIVE UP', {
      width: buttonWidth, height: 56, fontSize: 22, fill: PALETTE.panelRaised, onClick: () => this.giveUp(),
    });
  }

  private createInputHandlers(): void {
    // Single board-level click handler for both piece selection and move execution
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.puzzleData.completed || this.puzzleData.failed) return;

      const square = this.pixelToSquare(pointer.x, pointer.y);
      if (!square) return;

      // Check if clicked square has a piece of the player's color
      const board = this.chessGame.getBoard();
      const [file, rank] = this.squareToIndices(square);
      const piece = board[rank]?.[file];

      if (this.selectedSquare) {
        // A piece is already selected
        if (piece && piece.color === this.playerColor && square !== this.selectedSquare) {
          // Clicked another player piece — reselect
          this.selectSquare(square);
        } else {
          // Try to move to the clicked square
          this.attemptMove(this.selectedSquare, square);
        }
      } else {
        // No piece selected — try to select one
        if (piece && piece.color === this.playerColor) {
          this.selectSquare(square);
        }
      }
    });
  }

  private selectSquare(square: Square): void {
    this.selectedSquare = square;
    this.updateHighlights();
  }

  private updateHighlights(): void {
    if (!this.highlightGraphics) return;

    this.highlightGraphics.clear();

    if (!this.selectedSquare) return;

    // Highlight selected square
    const { x, y } = this.squareToPixel(this.selectedSquare);
    this.highlightGraphics.fillStyle(0xffff00, 0.3);
    this.highlightGraphics.fillRect(
      x - this.CELL_SIZE / 2,
      y - this.CELL_SIZE / 2,
      this.CELL_SIZE,
      this.CELL_SIZE
    );

    // Highlight legal moves
    const legalMoves = this.chessGame.getLegalMoves(this.selectedSquare);
    legalMoves.forEach(targetSquare => {
      const { x: targetX, y: targetY } = this.squareToPixel(targetSquare);
      this.highlightGraphics!.fillStyle(0x00ff00, 0.3);
      this.highlightGraphics!.fillCircle(targetX, targetY, this.CELL_SIZE / 6);
    });
  }

  private attemptMove(from: Square, to: Square): void {
    const line = this.puzzleData.puzzle.line;
    const idx = this.puzzleData.currentMoveIndex;
    const expected = line[idx];
    if (!expected) return;

    // Reject illegal moves silently (just deselect)
    const legalTargets = this.chessGame.getLegalMoves(from);
    if (!legalTargets.includes(to)) {
      this.selectedSquare = undefined;
      this.updateHighlights();
      return;
    }

    const isExpected = from === expected.from && to === expected.to;
    const move = this.chessGame.makeMove(from, to, isExpected ? expected.promotion : 'q');
    if (!move || !move.isLegal) {
      this.selectedSquare = undefined;
      this.updateHighlights();
      return;
    }

    this.selectedSquare = undefined;
    this.updateHighlights();
    this.createPieces();

    // Any move that delivers immediate checkmate solves the puzzle, even an
    // alternative mate the puzzle author did not script.
    if (this.chessGame.isCheckmate()) {
      this.puzzleData.playerMoves.push(`${from}-${to}`);
      this.puzzleData.currentMoveIndex = line.length;
      this.updateMoveList();
      this.puzzleData.completed = true;
      this.onPuzzleComplete();
      return;
    }

    if (!isExpected) {
      this.handleWrongMove(idx);
      return;
    }

    // Correct move, but mate not yet delivered: play the scripted reply
    this.puzzleData.playerMoves.push(`${from}-${to}`);
    this.puzzleData.currentMoveIndex++;
    this.updateMoveList();
    this.instructionText?.setText('Correct! Keep going...');
    this.instructionText?.setColor('#00ff00');

    const reply = line[this.puzzleData.currentMoveIndex];
    if (!reply) {
      // Line exhausted without mate (should not happen with verified data)
      this.puzzleData.completed = true;
      this.onPuzzleComplete();
      return;
    }

    this.time.delayedCall(600, () => {
      if (this.puzzleData.completed || this.puzzleData.failed) return;
      this.chessGame.makeMove(reply.from, reply.to, reply.promotion);
      this.puzzleData.currentMoveIndex++;
      this.createPieces();
      this.instructionText?.setText('Find the best move!');
      this.instructionText?.setColor('#ffffff');
    });
  }

  /**
   * Wrong move handling. The player gets three strikes: each bad move undoes
   * itself so they can try again, and the puzzle is only failed on the third
   * mistake. Solution Vision (puzzle_redo_once) grants one free retry that does
   * not cost a strike.
   */
  private handleWrongMove(atIndex: number): void {
    // Always undo the bad move so the player can keep trying from this position
    this.rebuildPosition(atIndex);

    // Dramatic wrong-answer feedback: smack + screen shake
    AudioManager.getInstance().play('smack');
    AudioManager.getInstance().haptic([0, 60]);
    this.cameras.main.shake(180, 0.01);

    // Solution Vision: one free retry that doesn't count against the strikes
    if (this.gameStateManager.hasActiveUpgrade('puzzle_redo_once') && !this.redoUsed) {
      this.redoUsed = true;
      this.instructionText?.setText('Not the best move — Solution Vision gives a free retry!');
      this.instructionText?.setColor('#ffaa00');
      return;
    }

    this.wrongMoves++;
    this.lightStrikeMark();

    if (this.wrongMoves >= this.MAX_STRIKES) {
      this.puzzleData.failed = true;
      this.chessClock.pause();
      this.instructionText?.setText('Three mistakes — puzzle failed.');
      this.instructionText?.setColor('#ff0000');
      this.time.delayedCall(1800, () => this.endPuzzle(false));
      return;
    }

    const remaining = this.MAX_STRIKES - this.wrongMoves;
    this.instructionText?.setText(`Not the best move! ${remaining} ${remaining === 1 ? 'try' : 'tries'} left — try again.`);
    this.instructionText?.setColor('#ffaa00');
  }

  /**
   * Rebuild the board from the puzzle FEN, replaying the solved part of the line.
   */
  private rebuildPosition(uptoIndex: number): void {
    this.chessGame.destroy();
    this.chessGame = new ChessGame(this.puzzleData.puzzle.fen, AI_DIFFICULTY[1], {
      minCol: 0,
      maxCol: 7,
      minRank: 1,
      maxRank: 8
    });
    for (let i = 0; i < uptoIndex; i++) {
      const m = this.puzzleData.puzzle.line[i];
      this.chessGame.makeMove(m.from, m.to, m.promotion);
    }
    this.createPieces();
  }

  private onTimeOut(): void {
    this.puzzleData.failed = true;

    // Show timeout message
    this.instructionText?.setText('Time\'s up! Puzzle failed.');
    this.instructionText?.setColor('#ff0000');

    // Show solution
    this.time.delayedCall(2000, () => {
      this.endPuzzle(false);
    });
  }

  private onPuzzleComplete(): void {
    this.chessClock.pause();

    // Show success message
    this.instructionText?.setText('Puzzle solved! Well done!');
    this.instructionText?.setColor('#00ff00');

    // Gold reward: base + difficulty bonus - hint penalty
    const tierBonus: Record<string, number> = { beginner: 0, easy: 10, medium: 20, hard: 30, expert: 45 };
    const hintPenalty = this.puzzleData.hintsUsed * 10;
    const totalGold = Math.max(10, 30 + (tierBonus[this.puzzleDifficulty] || 0) - hintPenalty);

    this.time.delayedCall(2000, () => {
      this.endPuzzle(true, totalGold);
    });
  }

  private showHint(): void {
    this.revealHint(true);
  }

  /**
   * Highlight the piece that should move. Counted hints reduce gold reward.
   */
  private revealHint(countsAgainstGold: boolean): void {
    const nextMove = this.puzzleData.puzzle.line[this.puzzleData.currentMoveIndex];

    if (!nextMove) {
      this.hintText?.setText('No more moves!');
      return;
    }

    if (countsAgainstGold) {
      this.puzzleData.hintsUsed++;
    }

    // Flash the square of the piece that should move
    const { x, y } = this.squareToPixel(nextMove.from);
    const flash = this.add.rectangle(x, y, this.CELL_SIZE, this.CELL_SIZE, 0xffaa00, 0.45);
    flash.setDepth(6);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 1600,
      onComplete: () => flash.destroy(),
    });

    this.hintText?.setText(
      `Hint: move the piece on ${nextMove.from}` +
      (countsAgainstGold ? ` (hints used: ${this.puzzleData.hintsUsed})` : '')
    );
  }

  private giveUp(): void {
    this.puzzleData.failed = true;
    this.chessClock.pause();

    this.instructionText?.setText('Puzzle abandoned.');
    this.instructionText?.setColor('#888888');

    this.time.delayedCall(1000, () => {
      this.endPuzzle(false);
    });
  }

  private endPuzzle(success: boolean, goldEarned: number = 0): void {
    console.log('[PuzzleScene] Puzzle ended. Success:', success, 'Gold:', goldEarned);

    // Award gold if successful
    if (success && goldEarned > 0) {
      this.gameStateManager.addGold(goldEarned);
    }

    // Puzzle Veteran: +10 gold for completing puzzles
    if (success) {
      const activeUpgrades = this.gameStateManager.getActiveUpgrades();
      const hasPuzzleVeteran = activeUpgrades.some(u => u.effect.value === 'puzzle_veteran');
      if (hasPuzzleVeteran) {
        this.gameStateManager.addGold(10);
      }
    }

    // Return to map
    this.scene.start('MapScene');
  }

  private updateClockDisplay(data: { playerTimeMs: number }): void {
    if (this.playerClockText) {
      const timeString = ChessClock.formatTime(data.playerTimeMs);
      this.playerClockText.setText(timeString);

      // Change color based on time remaining
      if (data.playerTimeMs < 30000) {
        this.playerClockText.setColor('#ff0000'); // Red when under 30 seconds
      } else if (data.playerTimeMs < 60000) {
        this.playerClockText.setColor('#ffaa00'); // Orange when under 1 minute
      } else {
        this.playerClockText.setColor('#00ff00'); // Green otherwise
      }
    }
  }

  private updateMoveList(): void {
    if (this.moveListText) {
      const movesText = this.puzzleData.playerMoves.join(', ');
      this.moveListText.setText(`Moves: ${movesText || '-'}`);
    }
  }

  private squareToPixel(square: Square): { x: number; y: number } {
    const file = square.charCodeAt(0) - 97; // 'a' = 0
    const rank = parseInt(square[1]) - 1; // '1' = 0

    // Flip board 180 degrees if playing as black
    const displayFile = this.playerColor === 'b' ? 7 - file : file;
    const displayRank = this.playerColor === 'b' ? rank : 7 - rank;

    return {
      x: this.BOARD_OFFSET_X + displayFile * this.CELL_SIZE + this.CELL_SIZE / 2,
      y: this.BOARD_OFFSET_Y + displayRank * this.CELL_SIZE + this.CELL_SIZE / 2
    };
  }

  private squareToIndices(square: Square): [number, number] {
    const file = square.charCodeAt(0) - 97; // 'a' = 0
    const rank = parseInt(square[1]) - 1; // '1' = 0
    return [file, 7 - rank]; // row index is inverted
  }

  private pixelToSquare(x: number, y: number): Square | null {
    let file = Math.floor((x - this.BOARD_OFFSET_X) / this.CELL_SIZE);
    let rank = 7 - Math.floor((y - this.BOARD_OFFSET_Y) / this.CELL_SIZE);

    // Flip coordinates if playing as black
    if (this.playerColor === 'b') {
      file = 7 - file;
      rank = 7 - rank;
    }

    if (file < 0 || file > 7 || rank < 0 || rank > 7) {
      return null;
    }

    return (String.fromCharCode(97 + file) + (rank + 1)) as Square;
  }

  private getActiveColorFromFen(fen: string): 'w' | 'b' {
    // FEN format: position activeColor castling enPassant halfmove fullmove
    // Example: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
    const parts = fen.split(' ');
    return parts[1] === 'b' ? 'b' : 'w';
  }

  shutdown(): void {
    // Clean up
    if (this.chessClock) {
      this.chessClock.destroy();
    }

    this.pieceSprites.clear();
    this.selectedSquare = undefined;
    this.highlightGraphics = undefined;
  }
}
